/**
 * The three calls the Search Programs page makes.
 *
 * Paths are relative, never absolute: the Vite dev server proxies /api to the
 * backend and nginx does the same in production, so there is no API base URL to
 * configure per environment and get wrong.
 */

/** Errors carrying the message the API chose, so the UI can show it verbatim. */
async function readError(res, fallback) {
  const body = await res.json().catch(() => ({}));
  return new Error(body.error || fallback);
}

export async function getOptions({ headers = {} } = {}) {
  const res = await fetch('/api/programs/options', { headers });
  if (!res.ok) throw await readError(res, 'Failed to load filters');
  return res.json();
}

/**
 * `body` is the search state as the API documents it.
 *
 * Deliberately no tenantId or nationality. Access scope is resolved server-side
 * from the caller's session (backend/src/session.js); it used to be sent from
 * the browser, which meant the browser chose what it was allowed to see. The
 * server ignores these keys in a body, but sending them at all invites someone
 * to start trusting them.
 */
export async function searchPrograms(body, { signal } = {}) {
  const res = await fetch('/api/programs/search', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    throw await readError(res, "We couldn't complete that search. Please try again.");
  }
  return res.json();
}

export async function getFreshness(domain) {
  const res = await fetch(`/api/${domain}/freshness`);
  if (!res.ok) throw new Error(`freshness ${res.status}`);
  return res.json();
}

/**
 * University search — the same catalogue as Search Programs, grouped by
 * university. Access scope (tenant + nationality) is resolved server-side from
 * the session exactly as it is for programs; see the note on searchPrograms.
 */
export async function getUniversityOptions() {
  const res = await fetch('/api/universities/options');
  if (!res.ok) throw await readError(res, 'Failed to load university filters');
  return res.json();
}

export async function searchUniversities(body, { signal } = {}) {
  const res = await fetch('/api/universities/search', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    throw await readError(res, "We couldn't complete that search. Please try again.");
  }
  return res.json();
}

/** The quick filters' Student Level -> Board -> Grading tree and test lists (course-finder-be src/chat/filter-options.js). */
export async function getChatFilterOptions({ headers = {} } = {}) {
  const res = await fetch('/api/chat/filter-options', { headers });
  if (!res.ok) throw new Error(`chat filter options ${res.status}`);
  return res.json();
}

export async function getChatStatus({ headers = {} } = {}) {
  const res = await fetch('/api/chat/status', { headers });
  if (!res.ok) throw new Error(`chat status ${res.status}`);
  return res.json();
}

/**
 * The full record behind a search result.
 *
 * Typesense returns only the fields a results row renders; opening a result
 * fetches a SAFE ALLOWLIST DTO built from the MongoDB document — never the raw
 * record, never contract / commission / routing / access-control fields, never
 * a `_id`. Same backend, same port as search: `/api/programs/:id` and
 * `/api/universities/:id` are routes on the one Express app
 * (course-finder-be/src/detail.js, mounted by src/server.js); MongoDB is opened
 * lazily and a database failure there never touches search.
 *
 * `id` is the id carried on the Typesense hit (`hit.id` for a course,
 * `university.id` for a university). The server re-checks that the record is in
 * the caller's access scope and answers 404 if it is not, so a tampered id
 * cannot reach another partner's record.
 */
export async function getProgramDetail(id, { signal } = {}) {
  const res = await fetch(`/api/programs/${encodeURIComponent(id)}`, { signal });
  if (!res.ok) throw await readError(res, "We couldn't load that program.");
  return res.json();
}

export async function getUniversityDetail(id, { signal } = {}) {
  const res = await fetch(`/api/universities/${encodeURIComponent(id)}`, { signal });
  if (!res.ok) throw await readError(res, "We couldn't load that university.");
  return res.json();
}

/**
 * One course's full record and its requirements — the two bodies the Program
 * Details modal reads — for the assistant's detail panel. `country` is the
 * student's nationality id, which some requirements (MOI) depend on.
 */
export async function getCourseDetail(id, { headers = {}, country = null, signal } = {}) {
  const qs = country ? `?country=${encodeURIComponent(country)}` : '';
  const [detail, requirements] = await Promise.all([
    fetch(`/api/programs/${encodeURIComponent(id)}`, { headers, signal }),
    fetch(`/api/programs/${encodeURIComponent(id)}/requirements${qs}`, { headers, signal }),
  ]);
  if (!detail.ok) throw await readError(detail, 'Course details are not available right now.');
  return {
    detail: await detail.json(),
    // Requirements are a second, optional request: the panel still shows the
    // course without them.
    requirements: requirements.ok ? await requirements.json() : null,
  };
}
